import React, { useState, useEffect } from 'react';
import './UserEditModal.css';
import DexcomTestModal from './DexcomTestModal';

interface UserEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  initialUser: any;
}

const UserEditModal: React.FC<UserEditModalProps> = ({ isOpen, onClose, onSave, initialUser }) => {
  const [formData, setFormData] = useState<any>({});
  const [isSaving, setIsSaving] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);

  useEffect(() => {
    if (initialUser && isOpen) {
      setFormData({
        name: initialUser.name || '',
        user_type: initialUser.user_type || 'Child',
        target_blood_glucose: initialUser.target_blood_glucose || 100,
        has_cgm_enabled: initialUser.has_cgm_enabled || false,
        dexcom_share_username: initialUser.dexcom_share_username || '',
        // Write-only. BOLUS_NOTES-8: the password is not on the profile row
        // any more, so there is nothing to prefill and nothing to read back.
        // Blank means "keep whatever is already in the credentials store".
        dexcom_share_password: '',
        profile_image_url: initialUser.profile_image_url || '',
      });
      if (initialUser.profile_image_url) {
        import('../services/api').then(({ bolusnotesApi }) => {
          bolusnotesApi.files.presign(initialUser.profile_image_url).then((res: any) => {
             setPreviewUrl(res.url);
          }).catch((err: any) => console.error("Could not presign avatar", err));
        });
      }
      
      if (initialUser.dexcom_share_username) {
        import('../services/api').then(({ bolusnotesApi }) => {
          bolusnotesApi.dexcom.getAudit(initialUser.dexcom_share_username).then(logs => {
             setAuditLogs(logs || []);
          }).catch(err => console.error("Failed to fetch dexcom audit", err));
        });
      }
    } else {
      setAuditLogs([]);
    }
  }, [initialUser, isOpen]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const { bolusnotesApi } = await import('../services/api');
      let finalData = { ...formData };

      // BOLUS_NOTES-8: the password never travels with the profile row.
      // Pull it out here; it goes to set_dexcom_credentials after the
      // profile is saved, and an empty value means "leave it alone".
      const newDexcomPassword: string = finalData.dexcom_share_password || '';
      delete finalData.dexcom_share_password;

      // Optional: Check if we are claiming a shared dexcom account
      if (finalData.has_cgm_enabled && finalData.dexcom_share_username && newDexcomPassword) {
        const isShared = await bolusnotesApi.dexcom.checkShared(finalData.dexcom_share_username);
        if (isShared) {
           const proceed = window.confirm(
             "Warning: That Dexcom username is actively sharing data across another trusted account profile. " + 
             "Are you absolutely sure you want to change its connection password? If the password is wrong, you will break the connection for everyone."
           );
           if (!proceed) {
             setIsSaving(false);
             return;
           }
        }
      }

      // Upload profile image if a new one was selected
      if (selectedFile) {
        const fileExt = selectedFile.name.split('.').pop() || 'jpg';
        const uniqueId = initialUser?.id || crypto.randomUUID();
        const path = `profiles/${uniqueId}.${fileExt}`;
        
        await bolusnotesApi.files.upload(path, selectedFile);
        finalData.profile_image_url = path;
        
        // If creating new user, enforce the generated ID so the path matches
        if (!initialUser?.id) {
           finalData.id = uniqueId;
        }
      }

      let savedProfileId: string | undefined = initialUser?.id;
      if (initialUser && initialUser.id) {
        await bolusnotesApi.profiles.update(initialUser.id, finalData);
      } else {
        const created: any = await bolusnotesApi.profiles.create(finalData);
        savedProfileId = created?.id || created?.user?.id || finalData.id;
      }

      // Store the password encrypted, separately from the profile row.
      // If this throws we surface it rather than swallowing it: a profile
      // saved without its credential is a profile that silently stops
      // being polled, which is exactly the failure BOLUS_NOTES-8 closed.
      if (newDexcomPassword && finalData.dexcom_share_username) {
        await bolusnotesApi.dexcom.setCredentials({
          username: finalData.dexcom_share_username,
          password: newDexcomPassword,
          server: finalData.dexcom_server || 'US',
          profile_id: savedProfileId,
        });
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save profile:', err);
      alert('Failed to save profile. Please see console.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content profile-modal">
        <div className="modal-header">
          <h2>{initialUser ? 'Edit User Profile' : 'Add New User'}</h2>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSave} className="modal-form">
          <div className="form-section">
            <h3>General Settings</h3>
            <div className="form-group">
              <label>Name</label>
              <input 
                type="text" 
                name="name" 
                value={formData.name || ''} 
                onChange={handleChange} 
                required 
              />
            </div>
            <div className="form-group row-group">
              <div className="half-width">
                <label>User Type</label>
                <select name="user_type" value={formData.user_type || 'Child'} onChange={handleChange}>
                  <option value="Child">Child</option>
                  <option value="Adult">Adult</option>
                  <option value="Pet">Pet</option>
                </select>
              </div>
              <div className="half-width">
                <label>Target BG (mg/dL)</label>
                <input 
                  type="number" 
                  name="target_blood_glucose" 
                  value={formData.target_blood_glucose || ''} 
                  onChange={handleChange} 
                />
              </div>
            </div>
            
            <div className="form-group avatar-group" style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '15px' }}>
               {previewUrl ? (
                 <img src={previewUrl} alt="Avatar Preview" style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }} />
               ) : (
                 <div style={{ width: '60px', height: '60px', borderRadius: '50%', backgroundColor: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>👤</div>
               )}
               <div>
                  <label>Profile Image</label>
                  <input type="file" accept="image/*" onChange={handleFileChange} />
               </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Dexcom CGM Polling</h3>
            <div className="form-group-checkbox">
              <input 
                type="checkbox" 
                id="has_cgm_enabled"
                name="has_cgm_enabled" 
                checked={formData.has_cgm_enabled || false} 
                onChange={handleChange} 
              />
              <label htmlFor="has_cgm_enabled">Enable Dexcom Share Polling</label>
            </div>
            
            {formData.has_cgm_enabled && (
              <div className="dexcom-fields">
                <p className="help-text">
                  Enter your Dexcom Share credentials. The background job will poll glucose readings every 5 minutes.
                </p>
                <div className="form-group">
                  <label>Share Username</label>
                  <input 
                    type="text" 
                    name="dexcom_share_username" 
                    value={formData.dexcom_share_username || ''} 
                    onChange={handleChange} 
                    required={formData.has_cgm_enabled}
                  />
                </div>
                <div className="form-group">
                  <label>Share Password</label>
                  <input 
                    type="password" 
                    name="dexcom_share_password" 
                    value={formData.dexcom_share_password || ''} 
                    onChange={handleChange}
                    autoComplete="new-password"
                    placeholder={initialUser?.dexcom_share_username ? 'Leave blank to keep the saved password' : ''}
                    required={formData.has_cgm_enabled && !initialUser?.dexcom_share_username}
                  />
                  <small style={{ color: 'var(--text-muted)' }}>
                    Stored encrypted in the platform credentials store, not on the profile.
                  </small>
                </div>
                
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowTestModal(true)}
                  style={{ marginTop: '10px' }}
                  disabled={!formData.dexcom_share_username || !formData.dexcom_share_password}
                >
                  Test Connection
                </button>

                {auditLogs.length > 0 && (
                  <div className="audit-log-box" style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--surface-secondary)', borderRadius: '8px', border: '1px solid var(--border-medium)' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9em', color: 'var(--text-secondary)' }}>Activity History (Shared Access)</h4>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85em', maxHeight: '150px', overflowY: 'auto' }}>
                      {auditLogs.map(log => (
                        <li key={log.id} style={{ marginBottom: '8px', borderBottom: '1px solid var(--border-light)', paddingBottom: '4px' }}>
                          <strong>{new Date(log.timestamp).toLocaleString()}</strong> - {log.action}<br/>
                          <span style={{ color: 'var(--text-muted)' }}>By: {log.user_email || log.kstack_user_id}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={isSaving}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>

      <DexcomTestModal 
        isOpen={showTestModal} 
        onClose={() => setShowTestModal(false)}
        dexcomUsername={formData.dexcom_share_username}
        dexcomPassword={formData.dexcom_share_password}
      />
    </div>
  );
};

export default UserEditModal;
